import { useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  X,
  Layers3,
  Plus,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MediaUploadField } from "./MediaUploadField";

const defaultState = {
  name: "",
  image: null,
  galleryImages: [],
  videos: [],
  ribbon: "",
  savings_pct: "0",
  tagline: "",
  included: [""],
  original_price: "",
  price: "",
  isActive: true,
  displayOrder: "0",
};

const getSeed = (combo) => {
  if (!combo) return defaultState;
  return {
    name: combo.name || "",
    image: combo.image ? { url: combo.image } : null,
    galleryImages: combo.galleryImages?.map(url => ({ url })) || [],
    videos: combo.videos?.map(url => ({ url })) || [],
    ribbon: combo.ribbon || "",
    savings_pct: String(combo.savings_pct || 0),
    tagline: combo.tagline || "",
    included: combo.included?.length ? combo.included : [""],
    original_price: String(combo.original_price || ""),
    price: String(combo.price || ""),
    isActive: combo.isActive !== false,
    displayOrder: String(combo.displayOrder || 0),
  };
};

export function ComboForm({ combo, onSubmit, onCancel }) {
  const initial = useMemo(() => getSeed(combo), [combo]);
  const [form, setForm] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(getSeed(combo));
  }, [combo]);

  const submit = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    const payload = {
      name: form.name.trim(),
      image: form.image?.url || "",
      galleryImages: form.galleryImages?.map(img => img.url).filter(Boolean) || [],
      videos: form.videos?.map(vid => vid.url).filter(Boolean) || [],
      ribbon: form.ribbon.trim(),
      savings_pct: Number(form.savings_pct),
      tagline: form.tagline.trim(),
      included: form.included.map(i => i.trim()).filter(Boolean),
      original_price: Number(form.original_price),
      price: Number(form.price),
      isActive: form.isActive,
      displayOrder: Number(form.displayOrder),
    };

    try {
      await onSubmit(payload);
      if (!combo) {
        setForm(defaultState);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const addIncludedItem = () => {
    setForm(curr => ({ ...curr, included: [...curr.included, ""] }));
  };

  const updateIncludedItem = (idx, val) => {
    const next = [...form.included];
    next[idx] = val;
    setForm(curr => ({ ...curr, included: next }));
  };

  const removeIncludedItem = (idx) => {
    setForm(curr => ({ ...curr, included: curr.included.filter((_, i) => i !== idx) }));
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-6 rounded-[24px] border border-[#e2e8f0] bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
    >
      <div className="relative flex flex-col gap-4 rounded-[20px] bg-[linear-gradient(135deg,#1e1b16_0%,#2a2217_55%,#3f3321_100%)] p-6 text-white md:flex-row md:items-end md:justify-between">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 hover:text-white cursor-pointer"
            aria-label="Close form"
            title="Cancel and close"
          >
            <X size={16} />
          </button>
        )}
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold-soft">
            <Sparkles size={14} />
            Combo CMS
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold">
            {combo ? "Edit combo" : "Add combo"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/80">
            Create bundles of products to display on the Combos page.
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <label className="md:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Combo Name
          </span>
          <input
            required
            value={form.name}
            onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <div className="md:col-span-2 space-y-6">
          <MediaUploadField
            title="Front Main Image"
            helperText="The primary image shown on combos."
            accept="image/jpeg,image/jpg,image/png,image/webp"
            value={form.image}
            onChange={(value) =>
              setForm((current) => ({ ...current, image: value }))
            }
            uploadFiles={async (files, onProgress) => {
              const { uploadImage } = await import("@/lib/productApi");
              return await uploadImage(files[0], onProgress);
            }}
          />

          <MediaUploadField
            title="Gallery Images"
            helperText="Add as many gallery images as you need."
            accept="image/jpeg,image/jpg,image/png,image/webp"
            multiple
            allowReorder
            value={form.galleryImages}
            onChange={(value) =>
              setForm((current) => ({
                ...current,
                galleryImages: value || [],
              }))
            }
            uploadFiles={async (files, onProgress) => {
              const { uploadImages } = await import("@/lib/productApi");
              return await uploadImages(files, onProgress);
            }}
          />

          <MediaUploadField
            title="Product Video"
            helperText="An optional high-quality video for this combo."
            accept="video/mp4,video/quicktime,video/webm"
            value={form.videos}
            multiple
            allowReorder
            onChange={(value) =>
              setForm((current) => ({ ...current, videos: value || [] }))
            }
            uploadFiles={async (files, onProgress) => {
              const { uploadVideo } = await import("@/lib/productApi");
              return await uploadVideo(files[0], onProgress);
            }}
          />
        </div>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Tagline (e.g., FLOWERS + TOYS)
          </span>
          <input
            value={form.tagline}
            onChange={(e) => setForm((c) => ({ ...c, tagline: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Ribbon Text (e.g., Cozy Choice)
          </span>
          <input
            value={form.ribbon}
            onChange={(e) => setForm((c) => ({ ...c, ribbon: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            MRP (Original Price)
          </span>
          <input
            required
            type="number"
            min="0"
            value={form.original_price}
            onChange={(e) => setForm((c) => ({ ...c, original_price: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Selling Price
          </span>
          <input
            required
            type="number"
            min="0"
            value={form.price}
            onChange={(e) => setForm((c) => ({ ...c, price: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Savings %
          </span>
          <input
            type="number"
            min="0"
            max="100"
            value={form.savings_pct}
            onChange={(e) => setForm((c) => ({ ...c, savings_pct: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>

        <label>
          <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[#64748b]">
            Display Order
          </span>
          <input
            type="number"
            value={form.displayOrder}
            onChange={(e) => setForm((c) => ({ ...c, displayOrder: e.target.value }))}
            className="mt-2 h-12 w-full rounded-[12px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
          />
        </label>
        
        <label className="md:col-span-2 flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((c) => ({ ...c, isActive: e.target.checked }))}
            className="h-5 w-5 accent-black"
          />
          <span className="text-sm font-semibold text-[#0f172a]">Combo is Active and visible</span>
        </label>
      </div>

      <section className="rounded-[20px] border border-[#e2e8f0] bg-white p-5">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#64748b]">
            <Layers3 size={16} />
            Included Products
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addIncludedItem}>
            <Plus size={16} /> Add Item
          </Button>
        </div>

        <div className="space-y-3">
          {form.included.map((item, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                value={item}
                placeholder="E.g., Teddy Bear"
                onChange={(e) => updateIncludedItem(idx, e.target.value)}
                className="h-10 flex-1 rounded-[8px] border border-[#e2e8f0] px-4 text-sm outline-none transition focus:border-[#0f172a]"
              />
              <button
                type="button"
                onClick={() => removeIncludedItem(idx)}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[8px] border border-[#e2e8f0] text-[#64748b] transition hover:bg-red-50 hover:text-red-600 hover:border-red-200"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {form.included.length === 0 && (
            <p className="text-sm text-gray-500">No items included yet.</p>
          )}
        </div>
      </section>

      <div className="flex justify-end pt-4 border-t border-[#e2e8f0]">
        <Button
          type="submit"
          className="h-12 rounded-[12px] bg-[#0f172a] px-8 font-semibold text-white transition hover:bg-black"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : combo ? "Save Changes" : "Create Combo"}
        </Button>
      </div>
    </form>
  );
}
